import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firestore } from '../database/firebase';

const COLLECTION_NAME = 'productAudits';

/**
 * Get all product audits with optional filters
 */
export const getAudits = async (filters = {}) => {
  try {
    const auditsRef = collection(firestore, COLLECTION_NAME);
    let q = query(auditsRef, orderBy('auditDate', 'desc'));

    // Apply filters
    if (filters.stage) {
      q = query(q, where('projectStage', '==', filters.stage));
    }
    if (filters.createdBy) {
      q = query(q, where('createdBy', '==', filters.createdBy));
    }
    if (filters.role) {
      q = query(q, where('role', '==', filters.role));
    }

    const querySnapshot = await getDocs(q);
    const audits = [];
    querySnapshot.forEach(doc => {
      audits.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Apply date range filter in memory if provided
    if (filters.dateFrom || filters.dateTo) {
      return audits.filter(audit => {
        if (!audit.auditDate) return false;
        const auditDate = audit.auditDate.toDate
          ? audit.auditDate.toDate()
          : new Date(audit.auditDate);
        if (filters.dateFrom && auditDate < filters.dateFrom) return false;
        if (filters.dateTo && auditDate > filters.dateTo) return false;
        return true;
      });
    }

    return audits;
  } catch (error) {
    throw error;
  }
};

/**
 * Get a single audit by ID
 */
export const getAuditById = async id => {
  try {
    const auditRef = doc(firestore, COLLECTION_NAME, id);
    const auditSnap = await getDoc(auditRef);

    if (auditSnap.exists()) {
      return {
        id: auditSnap.id,
        ...auditSnap.data(),
      };
    }
    return null;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new audit
 */
export const addAudit = async (auditData, user) => {
  try {
    const auditsRef = collection(firestore, COLLECTION_NAME);
    const newAudit = {
      ...auditData,
      createdBy: user.email || user.uid,
      role: user.role || 'client',
      createdAt: serverTimestamp(),
      auditDate: auditData.auditDate
        ? Timestamp.fromDate(new Date(auditData.auditDate))
        : serverTimestamp(),
    };

    const docRef = await addDoc(auditsRef, newAudit);
    return {
      id: docRef.id,
      ...newAudit,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Update an existing audit
 */
export const updateAudit = async (id, auditData) => {
  try {
    const auditRef = doc(firestore, COLLECTION_NAME, id);
    const updateData = {
      ...auditData,
      auditDate: auditData.auditDate
        ? auditData.auditDate instanceof Timestamp
          ? auditData.auditDate
          : Timestamp.fromDate(new Date(auditData.auditDate))
        : undefined,
    };

    // Remove undefined fields
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(auditRef, updateData);
    return { id, ...updateData };
  } catch (error) {
    throw error;
  }
};

/**
 * Delete an audit
 */
export const deleteAudit = async id => {
  try {
    const auditRef = doc(firestore, COLLECTION_NAME, id);
    await deleteDoc(auditRef);
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Get audits by product name
 */
export const getAuditsByProduct = async productName => {
  try {
    const auditsRef = collection(firestore, COLLECTION_NAME);
    const q = query(
      auditsRef,
      where('productName', '==', productName),
      orderBy('auditDate', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const audits = [];
    querySnapshot.forEach(doc => {
      audits.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return audits;
  } catch (error) {
    throw error;
  }
};

/**
 * Calculate risk level from audit data
 */
export const calculateRiskLevel = audit => {
  const risks = audit.risks || '';
  const gaps = audit.gaps || '';
  const riskCount = (risks.match(/\n/g) || []).length + (risks ? 1 : 0);
  const gapCount = (gaps.match(/\n/g) || []).length + (gaps ? 1 : 0);
  const totalIssues = riskCount + gapCount;

  if (totalIssues === 0) return 'Low';
  if (totalIssues <= 3) return 'Medium';
  if (totalIssues <= 6) return 'High';
  return 'Critical';
};

/**
 * Calculate readiness score from checklist items
 * @param {Array} checklistItems - Array of checklist items with scores
 * @returns {Object} - { score: number, maxScore: number, percentage: number }
 */
export const calculateReadinessScore = checklistItems => {
  if (!checklistItems || checklistItems.length === 0) {
    return { score: 0, maxScore: 0, percentage: 0 };
  }

  let totalScore = 0;
  let maxScore = 0;

  checklistItems.forEach(item => {
    const weight = item.weight || 1;
    maxScore += weight;
    if (item.completed) {
      totalScore += weight;
    }
  });

  const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

  return {
    score: totalScore,
    maxScore,
    percentage,
  };
};

/**
 * Get readiness level from percentage
 */
export const getReadinessLevel = percentage => {
  if (percentage >= 90) return { level: 'Excellent', color: 'green' };
  if (percentage >= 75) return { level: 'Good', color: 'blue' };
  if (percentage >= 60) return { level: 'Fair', color: 'yellow' };
  if (percentage >= 40) return { level: 'Needs Improvement', color: 'orange' };
  return { level: 'Critical', color: 'red' };
};
